import { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { useListOfferCategories } from '@givebacks/api/offers';
import { useBreakPoints } from 'core';
import { useSearchParams } from 'react-router-dom';
import {
  ActionIcon,
  Box,
  ButtonMantine,
  Group,
  Modal,
  SearchInput,
  Select,
  Stack,
  Text,
  Title,
} from 'ui';
import { useDebouncedCallback, useDisclosure } from 'ui/Hooks';

import { FilterIcon } from '../../../../assets/images/icons/FilterIcon';
import { InstallExtensionEarnCard } from '../../components/InstallExtensionCard';
import { rewardSortValidation } from '../../rewardFilterValidation';
import classes from './OnlineMerchantSearchHeader.module.css';

const OnlineSearchInput = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSearch = searchParams.get('name') ?? '';
  const [localSearch, setLocalSearch] = useState(currentSearch);

  const handleDebouncedSearch = useDebouncedCallback(async (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('name', value);
    } else {
      params.delete('name');
    }
    setSearchParams(params);
  }, 500);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setLocalSearch(e.target.value);
      handleDebouncedSearch(e.target.value);
    },
    [handleDebouncedSearch, setLocalSearch]
  );

  return (
    <SearchInput value={localSearch} onChange={handleSearchChange} placeholder="Search Merchants" />
  );
};

const CategorySelect = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get('category') ?? '';

  const { data } = useListOfferCategories({
    queryOptions: {
      searches: {
        dining: {
          value: false,
        },
      },
    },
  });

  const selectData = useMemo(() => {
    const list = data?.categories.map((category) => {
      return { value: category.uuid, label: category.name };
    });
    list?.unshift({ value: '', label: 'All Categories' });
    return list ?? [];
  }, [data?.categories]);

  const handleCategoryChange = useCallback(
    (value: string | null) => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set('category', value);
      } else {
        params.delete('category');
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  return (
    <Select
      value={categoryId}
      onChange={handleCategoryChange}
      data={selectData}
      placeholder="Filter by Categories"
    />
  );
};

const SortSelect = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchOrder = rewardSortValidation.parse(searchParams.get('order'));

  const handleSortChange = useCallback(
    (value: string | null) => {
      const nextParams = new URLSearchParams(searchParams);
      if (value) {
        nextParams.set('order', value);
      } else {
        nextParams.delete('order');
      }
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams]
  );

  return (
    <Select
      value={searchOrder}
      leftSection={<Text fz="sm">Sort By</Text>}
      onChange={handleSortChange}
      data={[
        { value: 'name', label: 'Alphabetically' },
        { value: 'popularity', label: 'Popularity' },
        { value: 'promotional_amount', label: 'Highest Cashback' },
        { value: 'created_at', label: 'Most Recent' },
      ]}
      allowDeselect={false}
    />
  );
};

const SearchFilters = () => {
  const [opened, handlers] = useDisclosure(false);

  return (
    <>
      <Box w={180} className={classes.sortSelect}>
        <SortSelect />
      </Box>
      <ActionIcon variant="gray" className={classes.searchFilters} onClick={handlers.open}>
        <FilterIcon />
      </ActionIcon>
      <Modal title={'Filter Merchant Offers'} keepMounted opened={opened} onClose={handlers.close}>
        <Stack gap={'lg'}>
          <SortSelect />
          <CategorySelect />
          <ButtonMantine fullWidth onClick={handlers.close}>
            Apply Filters
          </ButtonMantine>
        </Stack>
      </Modal>
    </>
  );
};

const OnlineMerchantSearchHeader = () => {
  const { isSmallScreen } = useBreakPoints();

  return (
    <Stack>
      <Group justify="space-between" wrap="nowrap">
        <Title order={isSmallScreen ? 3 : 2} fz={isSmallScreen ? 18 : 'h2'}>
          Online Rewards
        </Title>
        <SearchFilters />
      </Group>
      <InstallExtensionEarnCard />
      <OnlineSearchInput />
    </Stack>
  );
};

export { OnlineMerchantSearchHeader };
